import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layers, Users, GraduationCap, Briefcase, RefreshCw, Loader2, Inbox,
  BarChart3, Lock, UserPlus, Mail, AlertCircle, ArrowRight, CheckCircle2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  getPartnerBatches, getPartnerTciApplications, reviewPartnerTciApplication,
  getPartnerChangeRequests, reviewPartnerChangeRequest,
} from '@/api/stnApi';

const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const STATUS_CLS = {
  active: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  enrolling: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  completed: 'bg-muted text-muted-foreground border-border',
  draft: 'bg-muted text-muted-foreground border-border',
};

export default function PartnerBatches() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [linked, setLinked]   = useState(true);
  const [batches, setBatches] = useState([]);

  const load = () => {
    setLoading(true);
    getPartnerBatches()
      .then((res) => { setLinked(res?.linked !== false); setBatches(res?.batches || []); })
      .catch((e) => toast.error(e?.message || 'Could not load partnered batches'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Layers className="w-6 h-6 text-primary" /> Partnered Batches</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Registration analytics for batches your organization is partnered on.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={cn('w-4 h-4 mr-1.5', loading && 'animate-spin')} /> Refresh
        </Button>
      </div>

      {!loading && linked && <TciApplicationsPanel />}
      {!loading && linked && <ChangeRequestsPanel />}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-20 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : !linked ? (
        <Card><CardContent className="flex flex-col items-center py-16 text-center text-muted-foreground">
          <Lock className="w-10 h-10 mb-3 opacity-60" />
          <p className="font-medium text-foreground">No partnered batches linked yet</p>
          <p className="text-sm mt-1 max-w-md">When an admin attaches your organization as a partner to a batch, its registration analytics appear here. Make sure your login email matches the one shared with the InternNexus team.</p>
        </CardContent></Card>
      ) : batches.length === 0 ? (
        <Card><CardContent className="flex flex-col items-center py-16 text-muted-foreground">
          <Inbox className="w-10 h-10 mb-3 opacity-60" />
          <p className="font-medium text-foreground">No batches shared with you yet</p>
        </CardContent></Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {batches.map((b) => (
            <Card
              key={b.id}
              className="hover:border-primary/40 transition-colors cursor-pointer"
              onClick={() => navigate(`/assigned-batches/${b.id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <Badge variant="outline" className="text-[10px]">
                    {b.platform === 'InternNexus' ? <Briefcase className="w-3 h-3 mr-1" /> : <GraduationCap className="w-3 h-3 mr-1" />}{b.platform}
                  </Badge>
                  <Badge variant="outline" className={cn('text-[10px] capitalize', STATUS_CLS[String(b.status).toLowerCase()] || STATUS_CLS.draft)}>{b.status}</Badge>
                  {b.code && <span className="text-[11px] text-muted-foreground font-mono">{b.code}</span>}
                </div>
                <h3 className="font-semibold truncate">{b.title}</h3>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{b.programs?.join(', ') || 'No program linked'}</p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground mt-3">
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {b.seats} seats</span>
                  <span className="flex items-center gap-1">{fmtDate(b.starts)}</span>
                  <span className="ml-auto flex items-center gap-1 text-primary font-medium"><BarChart3 className="w-3.5 h-3.5" /> Open report</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Pending public TCI applications across this partner's batches ────────────
function TciApplicationsPanel() {
  const [apps, setApps]   = useState(null);
  const [busy, setBusy]   = useState(null);

  const load = () => {
    getPartnerTciApplications('pending')
      .then((res) => setApps(res?.applications || []))
      .catch(() => setApps([]));
  };
  useEffect(() => { load(); }, []);

  if (apps === null) return null;
  if (apps.length === 0) return null;

  const review = async (a, action) => {
    setBusy(a._id);
    try {
      await reviewPartnerTciApplication(a._id, action, '');
      toast.success(action === 'accept' ? 'Accepted — activation link emailed to the applicant' : 'Application rejected');
      setApps((p) => (p || []).filter((x) => x._id !== a._id));
    } catch (e) {
      toast.error(e?.response?.data?.err?.msg || e?.message || 'Could not update application');
    } finally { setBusy(null); }
  };

  return (
    <Card className="mb-5 border-primary/30">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center"><UserPlus className="w-4 h-4 text-primary" /></div>
          <div>
            <h3 className="font-semibold text-sm">New applications</h3>
            <p className="text-[11px] text-muted-foreground">People who applied to your TCI programs without an account. Accept to email them a set-up link.</p>
          </div>
          <Badge variant="outline" className="ml-auto text-[10px] bg-primary/10 text-primary border-primary/20">{apps.length} pending</Badge>
        </div>
        <div className="space-y-2">
          {apps.map((a) => {
            const b = busy === a._id;
            return (
              <div key={a._id} className="rounded-xl border border-border p-3 flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-sm font-semibold flex items-center gap-1.5">{a.name} <span className="text-[11px] font-normal text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" />{a.email}</span></p>
                  <p className="text-[11px] text-muted-foreground">{a.program_name || a.program_ref} · {a.batch_title || a.batch_code}{a.phone ? ` · ${a.phone}` : ''} · {fmtDateTime(a.createdAt)}</p>
                  {(a.college_name || a.course || a.university_name) && (
                    <p className="text-[11px] text-muted-foreground/80 mt-0.5">{[a.course, a.college_name, a.university_name].filter(Boolean).join(' · ')}{a.university_reg_no ? ` · Reg: ${a.university_reg_no}` : ''}</p>
                  )}
                  {a.why_interested && <p className="text-[11px] text-muted-foreground mt-0.5 italic">"{a.why_interested}"</p>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button variant="outline" size="sm" className="h-7 text-xs text-rose-500 border-rose-500/30 hover:bg-rose-500/10" disabled={b} onClick={() => review(a, 'reject')}>Reject</Button>
                  <Button size="sm" className="h-7 text-xs gap-1" disabled={b} onClick={() => review(a, 'accept')}>
                    {b ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Accept
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Application change requests (program/batch changes by enrolled interns) ──
function ChangeRequestsPanel() {
  const [changeReqs, setChangeReqs] = useState([]);
  const [busyReq, setBusyReq]       = useState(null);
  const [rejecting, setRejecting]   = useState(null); // "enrollmentId:reqId"
  const [rejectNote, setRejectNote] = useState('');

  const load = () => {
    getPartnerChangeRequests()
      .then((r) => setChangeReqs(r?.requests || []))
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const reviewReq = async (req, action, note) => {
    if (action === 'reject' && !note) { setRejecting(`${req.enrollmentId}:${req.reqId}`); return; }
    setBusyReq(`${req.enrollmentId}:${req.reqId}`);
    try {
      const r = await reviewPartnerChangeRequest(req.enrollmentId, req.reqId, action, note);
      toast.success(action === 'approve' ? 'Change accepted & applied' : 'Change rejected');
      setRejecting(null); setRejectNote('');
      setChangeReqs((p) => p.map((x) =>
        (x.enrollmentId === req.enrollmentId && x.reqId === req.reqId)
          ? { ...x, status: r?.status || (action === 'approve' ? 'accepted' : 'rejected'), reviewNote: note || '' }
          : x
      ));
    } catch (e) { toast.error(e?.message || 'Failed'); }
    finally { setBusyReq(null); }
  };

  if (changeReqs.length === 0) return null;

  const openCount = changeReqs.filter((r) => ['applied', 'under_review'].includes(r.status)).length;

  return (
    <Card className="mb-5 border-amber-500/30 bg-amber-500/[0.03]">
      <CardContent className="p-4">
        <p className="text-sm font-semibold flex items-center gap-2 mb-3">
          <AlertCircle className="w-4 h-4 text-amber-500" /> Application change requests
          {openCount > 0 && <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/25 text-[10px]">{openCount} to review</Badge>}
          <span className="text-[10px] text-muted-foreground ml-auto">Applied → Under Review → Accepted / Rejected</span>
        </p>
        <div className="space-y-2.5">
          {changeReqs.map((r) => {
            const key  = `${r.enrollmentId}:${r.reqId}`;
            const busy = busyReq === key;
            const open = ['applied', 'under_review'].includes(r.status);
            const isRejecting = rejecting === key;
            return (
              <div key={key} className="rounded-lg border border-border bg-card p-3">
                <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{r.studentName || 'Intern'} <span className="text-[11px] font-normal text-muted-foreground">{r.studentEmail}</span></p>
                    <p className="text-[10px] text-muted-foreground">{r.programName} · requested {new Date(r.requestedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                  </div>
                  {open ? (
                    <div className="flex items-center gap-1.5">
                      <Button size="sm" variant="outline" className="h-7 text-xs text-rose-500 border-rose-500/30" disabled={busy} onClick={() => reviewReq(r, 'reject')}>Reject</Button>
                      <Button size="sm" className="h-7 text-xs bg-emerald-500 hover:bg-emerald-600 text-white" disabled={busy} onClick={() => reviewReq(r, 'approve')}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Accept'}
                      </Button>
                    </div>
                  ) : (
                    <Badge variant="outline" className={cn('text-[10px] capitalize',
                      r.status === 'accepted' ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/25' : 'bg-rose-500/10 text-rose-600 border-rose-500/25')}>
                      {r.status}
                    </Badge>
                  )}
                </div>
                <div className="space-y-1">
                  {(r.changes || []).map((c, j) => (
                    <p key={j} className="text-xs flex items-center gap-1.5 flex-wrap">
                      <span className="font-medium">{c.label || c.field}:</span>
                      <span className="text-muted-foreground line-through">{String(c.old || '—')}</span>
                      <ArrowRight className="w-3 h-3 text-muted-foreground" />
                      <span className="text-foreground">{String(c.new || '—')}</span>
                    </p>
                  ))}
                </div>
                {r.reason && <p className="text-[11px] text-muted-foreground mt-1.5">Reason: {r.reason}</p>}
                {open && <Badge variant="outline" className="text-[9px] capitalize mt-1.5">{r.status === 'under_review' ? 'Under review' : 'Applied'}</Badge>}
                {r.status === 'rejected' && r.reviewNote && <p className="text-[11px] text-rose-600 mt-1.5">Rejected: {r.reviewNote}</p>}
                {isRejecting && (
                  <div className="mt-2 flex items-center gap-2">
                    <Input value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="Reason for rejection (required)" className="h-8 text-xs" />
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setRejecting(null); setRejectNote(''); }}>Cancel</Button>
                    <Button size="sm" className="h-8 text-xs bg-rose-500 hover:bg-rose-600 text-white" disabled={!rejectNote.trim() || busy} onClick={() => reviewReq(r, 'reject', rejectNote.trim())}>
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Confirm reject'}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
